import type { StickerInstance, NameTag } from './sticker-types';

export type SkinRarity = 'consumer' | 'industrial' | 'mil-spec' | 'restricted' | 'classified' | 'covert' | 'contraband' | 'special';
export type WearLevel = 'factory-new' | 'minimal-wear' | 'field-tested' | 'well-worn' | 'battle-scarred';
export type SkinMethod = 'texture-swap' | 'pattern-paint' | 'custom-finish' | 'model-swap';

export interface WeaponSkinDefinition {
  id: number;
  name: string;
  weaponIds: string[];
  collection: string;
  rarity: SkinRarity;
  method: SkinMethod;
  textures?: {
    color?: string;
    normal?: string;
    orm?: string;
    emissive?: string;
    wearMask?: string;
  };
  pattern?: {
    texturePath: string;
    seedRange: [number, number];
    colorPalette: number[];
  };
  modelPath?: string;
  tint?: number;
  roughness?: number;
  metalness?: number;
  emissiveColor?: number;
  emissiveIntensity?: number;
  wearRange: [number, number];
  thumbnailPath: string;
  team?: 'T' | 'CT' | null;
  statTrakEligible: boolean;
  description?: string;
}

export interface WeaponSkinInstance {
  instanceId: string;
  skinId: number;
  weaponId: string;
  wearFloat: number;
  patternSeed: number;
  statTrakKills: number;  // -1 = not StatTrak
  nameTag: string;
}

export interface WeaponLoadout {
  equipped: Record<string, number>;  // weaponId -> skinId (0 = default)
  /**
   * C4b: Per-weapon equipped stickers. weaponId -> 5-slot array.
   * Slots that are null have no sticker. Optional — viewers should treat
   * absent entries as "no stickers equipped".
   */
  stickers?: Record<string, (StickerInstance | null)[]>;
  /** C4b: Per-weapon name tags. weaponId -> name tag (or absent). */
  nameTags?: Record<string, NameTag>;
  /**
   * Per-weapon wear float for the equipped skin instance (0.0–1.0).
   * Falls back to the midpoint of the skin's wearRange when absent so
   * the viewmodel renders visible wear instead of always-Factory-New.
   */
  wearFloats?: Record<string, number>;
  /**
   * F-CM-007: per-weapon StatTrak kill count for the equipped instance.
   * Absent or `< 0` means the equipped instance is not StatTrak — viewmodel
   * detaches the LED display in that case. The kill-event handler increments
   * this in place when the local player kills with the corresponding weapon
   * so the count survives weapon switches within a match.
   */
  statTrakKills?: Record<string, number>;
}

// Rarity colors for UI
export const RARITY_COLORS: Record<SkinRarity, number> = {
  'consumer': 0xb0c3d9,
  'industrial': 0x5e98d9,
  'mil-spec': 0x4b69ff,
  'restricted': 0x8847ff,
  'classified': 0xd32ce6,
  'covert': 0xeb4b4b,
  'contraband': 0xe4ae39,
  'special': 0xe4ae39,
};

export function wearLevelFromFloat(wearFloat: number): WearLevel {
  if (wearFloat < 0.07) return 'factory-new';
  if (wearFloat < 0.15) return 'minimal-wear';
  if (wearFloat < 0.38) return 'field-tested';
  if (wearFloat < 0.45) return 'well-worn';
  return 'battle-scarred';
}

export function wearLevelDisplayName(level: WearLevel): string {
  switch (level) {
    case 'factory-new': return 'Factory New';
    case 'minimal-wear': return 'Minimal Wear';
    case 'field-tested': return 'Field-Tested';
    case 'well-worn': return 'Well-Worn';
    case 'battle-scarred': return 'Battle-Scarred';
  }
}
